import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Button, Space } from 'antd'
import { Archive, ArrowLeft, FileText, Pencil, Receipt } from 'lucide-react'
import type { RentalOrder } from '@super-mega/server/contracts/order'
import { OrderBillsDrawer } from '@/features/bill/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { OrderNotesEditModal } from './order-notes-edit-modal.tsx'
import { RentalArchiveModal } from './rental-archive-modal.tsx'
import { RentalLifecycleActions } from './rental-lifecycle-actions.tsx'
import type { RentalListSearch } from './rental-list-search.ts'

export function RentalDetailActions({ order, search }: { order: RentalOrder; search: RentalListSearch }) {
  const access = useAccess()
  const navigate = useNavigate()
  const [viewingBills, setViewingBills] = useState(false)
  const [editingNotes, setEditingNotes] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const notesOnly = Boolean(order.archivedAt) || ['ended', 'cancelled', 'archived'].includes(order.status)
  const edit = () => {
    if (notesOnly) {
      setEditingNotes(true)
      return
    }
    void navigate({
      to: '/orders/rentals/edit/$rentalId',
      params: { rentalId: String(order.id) },
      search: {},
      state: { rentalListReturnSearch: search }
    })
  }
  return (
    <>
      <footer
        aria-label="租赁订单操作"
        className="border-border bg-surface/95 bottom-3 z-20 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 shadow-md backdrop-blur sm:sticky"
      >
        <Button
          icon={<ArrowLeft aria-hidden size={16} />}
          onClick={() => void navigate({ to: '/orders/rentals', search })}
        >
          返回租赁列表
        </Button>
        <Space wrap>
          <RentalLifecycleActions order={order} />
          {access.can('order.rental.update') && (
            <Button icon={<Pencil aria-hidden size={16} />} onClick={edit}>
              {notesOnly ? '补充备注与附件' : '编辑订单'}
            </Button>
          )}
          {access.can('bill.read') && (
            <Button icon={<Receipt aria-hidden size={16} />} onClick={() => setViewingBills(true)}>
              查看账单与收款
            </Button>
          )}
          {access.visit(`/orders/rentals/contract/${order.id}`) && (
            <Button
              href={`/orders/rentals/contract/${order.id}`}
              icon={<FileText aria-hidden size={16} />}
              rel="noopener noreferrer"
              target="_blank"
              type="primary"
            >
              {order.status === 'pending' ? '生成合同' : '查看合同 / 打印'}
            </Button>
          )}
          {access.can('order.rental.archive') && !order.archivedAt && ['ended', 'cancelled'].includes(order.status) && (
            <Button icon={<Archive aria-hidden size={16} />} onClick={() => setArchiving(true)}>
              归档订单
            </Button>
          )}
        </Space>
      </footer>
      {viewingBills && (
        <OrderBillsDrawer
          order={{ type: 'rental', id: order.id, orderNo: order.orderNo }}
          onClose={() => setViewingBills(false)}
        />
      )}
      {editingNotes && <OrderNotesEditModal kind="rental" order={order} onClose={() => setEditingNotes(false)} />}
      {archiving && <RentalArchiveModal rentalOrderId={order.id} onClose={() => setArchiving(false)} />}
    </>
  )
}
